import Moment from 'moment';
import { get as _get, merge as _merge } from 'lodash';

import { FetchAPI, ParamsSerializer } from './auth-service';
import ReportsService from './reports-service';
import { Acronym, SaveFile, FilterToEnglish } from '../libraries/helpers';

const defaults = Object.entries({
    count: {
        variable: 'count',
        sortable: true,
        title: 'Count',
        field: 'count',
        groupable: false,
        indexed: false,
        addon: true,
        order: -2,
        grouping: true,
        hide: true,
        jsonHidden: true,
        sticky: true,
    },
    activity: {
        variable: 'activity',
        sortable: false,
        title: 'Activity',
        field: 'activity',
        groupable: false,
        indexed: false,
        addon: true,
        order: -1,
        grouping: true,
        hide: true,
        jsonHidden: true,
        sticky: true,
    },
    remove: {
        variable: 'remove',
        sortable: false,
        title: ' ',
        field: 'remove',
        groupable: false,
        resizable: false,
        indexed: false,
        addon: true,
        order: -1,
        hide: true,
        jsonHidden: true,
        disableClickEventBubbling: true,
    },
    timestamp: {
        variable: 'timestamp',
        hide: false,
        type: 'date',
        title: 'Timestamp',
        getter: d => _get(d, '_source.bt_uuid') === undefined
            ? _get(d, '_source.timestamp') : _get(d, '_source.bro_log.orig_ts')
    },
    // only available for bt mode
    db_timestamp: {
        variable: 'db_timestamp',
        hide: true,
        type: 'date',
        title: 'DB Timestamp',
        sticky: true,
        sortable: true,
        columnSelectorHidden: true,
        getter: d => _get(d, '_source.timestamp'),
    },
    'bro_log.proto': {
        variable: 'bro_log.proto',
        hide: false,
        type: 'str',
        title: 'Proto',
        getter: d => (_get(d, '_source.bro_log.proto') || '').toString().toUpperCase(),
    },
    'bro_log.service': {
        variable: 'bro_log.service',
        hide: false,
        type: 'str',
        title: 'Service',
    },
    'bro_log.id.orig_h': {
        variable: 'bro_log.id.orig_h',
        title: 'Orig IP',
        hide: false,
        type: 'ip',
        getter: d => _get(d, '_source.bro_log', {})['id.orig_h'] // FU zeek
    },
    'bro_log.id.orig_p': {
        variable: 'bro_log.id.orig_p',
        title: 'Orig Port',
        hide: false,
        type: 'port',
        getter: d => _get(d, '_source.bro_log', {})['id.orig_p'] // FU zeek
    },
    'bro_log.id.resp_h': {
        variable: 'bro_log.id.resp_h',
        title: 'Resp IP',
        hide: false,
        type: 'ip',
        getter: d => _get(d, '_source.bro_log', {})['id.resp_h'] // FU zeek
    },
    'bro_log.id.resp_p': {
        variable: 'bro_log.id.resp_p',
        title: 'Resp Port',
        hide: false,
        type: 'port',
        getter: d => _get(d, '_source.bro_log', {})['id.resp_p'] // FU zeek
    },
    'bricata.sensor_uuid': {
        variable: 'bricata.sensor_uuid',
        title: 'Sensor',
        hide: false,
        type: 'sensor_uuid',
    },
    'bricata.tag': {
        variable: 'bricata.tag',
        hide: false,
        type: 'tag',
        title: 'Tag',
        sticky: true,
    },
    'bricata.mitre_category': {
        variable: 'bricata.mitre_category',
        type: 'mitre',
        title: 'ATT&CK Category',
        sticky: true,
        gator: 'none',
    },
    ips: {
        variable: 'ips',
        title: 'IP address Pair',
        tableHidden: true,
    },
    all: {
        variable: 'all',
        tableHidden: true,
        indexed: false,
    },
    answers: {
        variable: 'answers',
        title: 'DNS Response',
        tableHidden: true,
        indexed: false,
    },
    src_ip: {
        variable: 'src_ip',
        tableHidden: true,
        indexed: false,
    },
    dest_ip: {
        variable: 'dest_ip',
        tableHidden: true,
        indexed: false,
    },
    event_type: {
        variable: 'event_type',
        tableHidden: true,
        indexed: false,
    },
    'bricata.event_uuid': {
        variable: 'bricata.event_uuid',
        groupable: false,
    },
    'bro_log.irt': {
        variable: 'bro_log.irt',
        tableHidden: true,
        indexed: false,
    },
    // fu for using '.' in json fields
    'bro_log.seen.indicator': {
        variable: 'bro_log.seen.indicator',
        title: 'Seen Indicator',
        indexed: true,
        hide: true,
        getter: d => _get(d, '_source.bro_log', {})['seen.indicator'] // FU zeek
    },
    'bro_log.seen.indicator_type': {
        variable: 'bro_log.seen.indicator_type',
        title: 'Seen Indicator Type',
        indexed: true,
        hide: true,
        getter: d => _get(d, '_source.bro_log', {})['seen.indicator_type'] // FU zeek
    },
    'bro_log.seen.node': {
        variable: 'bro_log.seen.node',
        title: 'Seen Node',
        indexed: true,
        hide: true,
        getter: d => _get(d, '_source.bro_log', {})['seen.node'] // FU zeek
    },
    'bro_log.seen.where': {
        variable: 'bro_log.seen.where',
        title: 'Seen Where',
        indexed: true,
        hide: true,
        getter: d => _get(d, '_source.bro_log', {})['seen.where'] // FU zeek
    },
    // this is synonymous to event_source, coming from Bro
    file_name: {
        variable: 'file_name',
        tableHidden: true,
        indexed: true,
        title: 'file_name',
        jsonHidden: true,
    },
    'bricata.event_format': {
        variable: 'bricata.event_format',
        tableHidden: true,
        indexed: false,
        jsonHidden: true,
    },
    'bricata.sensor_ipv4': {
        variable: 'bricata.sensor_ipv4',
        tableHidden: true,
        indexed: false,
    },
    'bricata.sensor_ipv6': {
        variable: 'bricata.sensor_ipv6',
        tableHidden: true,
        indexed: false,
    },
    'bricata.dest_location.location.lat': {
        variable: 'bricata.dest_location.location.lat',
        // indexed: false,
        // jsonHidden: true,
        hide: true,
        title: 'Dest Latitude',
    },
    'bricata.dest_location.location.lon': {
        variable: 'bricata.dest_location.location.lon',
        // indexed: false,
        // jsonHidden: true,
        hide: true,
        title: 'Dest Longitude',
    },
    'bricata.src_location.location.lat': {
        variable: 'bricata.src_location.location.lat',
        // indexed: false,
        // jsonHidden: true,
        hide: true,
        title: 'Src Latitude',
    },
    'bricata.src_location.location.lon': {
        variable: 'bricata.src_location.location.lon',
        // indexed: false,
        // jsonHidden: true,
        hide: true,
        title: 'Src Longitude',
    },
    'bricata.src_location.location': {
        variable: 'bricata.src_location.location',
        // indexed: false,
        // jsonHidden: true,
        tableHidden: true,
    },
    'bricata.dest_location.city_name': {
        variable: 'bricata.dest_location.city_name',
        title: 'Resp City',
        hide: true,
    },
    'bricata.dest_location.country': {
        variable: 'bricata.dest_location.country',
        title: 'Resp Country',
        hide: true,
    },
    'bricata.dest_location.state': {
        variable: 'bricata.dest_location.state',
        title: 'Resp State',
        hide: true,
    },
    'bricata.src_location.city_name': {
        variable: 'bricata.src_location.city_name',
        title: 'Orig City',
        hide: true,
    },
    'bricata.src_location.country': {
        variable: 'bricata.src_location.country',
        title: 'Orig Country',
        hide: true,
    },
    'bricata.src_location.state': {
        variable: 'bricata.src_location.state',
        title: 'Orig State',
        hide: true,
    },
    'bricata.event_source': {
        variable: 'bricata.event_source',
        title: 'Data Source',
        hide: true,
    },
    'bro_log.pcr': {
        variable: 'bro_log.pcr',
        title: 'PCR',
        type: 'pcr',
        hide: true,
    },
    'bro_log.fuid': {
        variable: 'bro_log.fuid',
        title: 'File UID',
        hide: true,
    },
    'bro_log.uid': {
        variable: 'bro_log.uid',
        title: 'Session UID',
        hide: true,
        indexed: true,
        groupable: true,
    },
    'bro_log.conn_uids': {
        variable: 'bro_log.conn_uids',
        title: 'Connection UID',
        hide: true,
        indexed: true,
        groupable: true,
    },
    'bro_log.uids': {
        variable: 'bro_log.uids',
        title: 'UIDs',
        hide: true,
        indexed: true,
        groupable: true,
    },
    'bro_log.query': {
        variable: 'bro_log.query',
        title: 'DNS Request',
        hide: true,
    },
    'bro_log.query.txt': {
        variable: 'bro_log.query.txt',
        title: 'DNS Request(txt)',
        hide: true,
    },
    'bro_log.answers': {
        variable: 'bro_log.answers',
        title: 'DNS Response',
        hide: true,
    },
    'bro_log.answers.txt': {
        variable: 'bro_log.answers.txt',
        title: 'DNS Response(txt)',
        hide: true,
    },
    'bro_log.vlan': {
        variable: 'bro_log.vlan',
        title: 'VLAN',
        hide: true,
    },
    'bro_log.inner_vlan': {
        variable: 'bro_log.inner_vlan',
        title: 'Inner VLAN',
        hide: true,
    },
    'bro_log.md5': {
        variable: 'bro_log.md5',
        title: 'MD5',
        type: 'file_hash',
        hide: true,
    },
    'bro_log.sha1': {
        variable: 'bro_log.sha1',
        title: 'SHA1',
        type: 'file_hash',
        hide: true,
    },
    'bro_log.sha256': {
        variable: 'bro_log.sha256',
        title: 'SHA256',
        type: 'file_hash',
        hide: true,
    },
    'bro_log.x_originating_ip': {
        variable: 'bro_log.x_originating_ip',
        indexed: true,
        hide: true,
        type: 'ip',
        title: 'xOriginating IP',
    },
    'bro_log.xorig': {
        variable: 'bro_log.xorig',
        indexed: true,
        hide: true,
        type: 'ip',
        title: 'xOrig',
    },
    'bro_log.tx_hosts': {
        variable: 'bro_log.tx_hosts',
        indexed: true,
        hide: true,
        type: 'ip',
        title: 'Tx Hosts',
    },
    'bro_log.tx_port': {
        variable: 'bro_log.tx_port',
        indexed: true,
        hide: true,
        type: 'port',
        title: 'Tx Port',
    },
    'bro_log.rx_hosts': {
        variable: 'bro_log.rx_hosts',
        indexed: true,
        hide: true,
        type: 'ip',
        title: 'Rx Hosts',
    },
    'bro_log.rx_port': {
        variable: 'bro_log.rx_port',
        indexed: true,
        hide: true,
        type: 'port',
        title: 'Rx Port',
    },
    bt_uuid: {
        variable: 'bt_uuid',
        indexed: true,
        title: 'Backtesting Job',
        hide: true,
        sortable: true,
        groupable: true,
        gator: 'bt_uuid',
        type: 'bt_job_uuid',
    },
    // hide legacy field
    'bricata.bt_uuid': {
        variable: 'bricata.bt_uuid',
        indexed: false,
        tableHidden: true,
        // jsonHidden: true,
    },
    'bricata.bt_pcap_name': {
        variable: 'bricata.bt_pcap_name',
        indexed: false,
        title: 'BT PCAP Name',
        hide: true,
    },
    // backtesting fields
    'backtesting.uuid': {
        variable: 'backtesting.uuid',
        title: 'Backtesting Job',
        indexed: false,
        hide: true,
        tableHidden: true,
    },
    'backtesting.data.pcapdirs': {
        variable: 'backtesting.data.pcapdirs',
        title: 'PCAP Dirs',
        indexed: false,
        hide: true,
        tableHidden: true,
    },
    'backtesting.created': {
        variable: 'backtesting.created',
        indexed: false,
        hide: true,
        type: 'date',
    },
    'backtesting.updated': {
        variable: 'backtesting.updated',
        indexed: false,
        hide: true,
        type: 'date',
    },
    'backtesting.queued': {
        variable: 'backtesting.queued',
        indexed: false,
        hide: true,
        type: 'date',
    },
    'backtesting.pushed': {
        variable: 'backtesting.pushed',
        indexed: false,
        hide: true,
        type: 'date',
    },
    'backtesting.completed': {
        variable: 'backtesting.completed',
        indexed: false,
        hide: true,
        type: 'date',
    },
    'backtesting.data.start': {
        variable: 'backtesting.data.start',
        title: 'Data Range Start',
        indexed: false,
        hide: true,
        type: 'date',
    },
    'backtesting.data.end': {
        variable: 'backtesting.data.end',
        title: 'Data Range End',
        indexed: false,
        hide: true,
        type: 'date',
    },
    'backtesting.data.timefilter_start': {
        variable: 'backtesting.data.timefilter_start',
        indexed: false,
        hide: true,
        type: 'date',
    },
    'backtesting.data.timefilter_end': {
        variable: 'backtesting.data.timefilter_end',
        indexed: false,
        hide: true,
        type: 'date',
    },
    'backtesting.data.timestamp': {
        variable: 'backtesting.data.timestamp',
        indexed: false,
        hide: true,
        type: 'date',
    },
    // entry for json fields in detail view
    'bro_log.orig_ts': {
        field: 'bro_log.orig_ts',
        hide: true,
        title: 'Orig Timestamp',
        type: 'date',
        indexed: false,
        tableHidden: true,
    },
    // redundant
    'backtesting.data.job_uuid': {
        field: 'backtesting.data.job_uuid',
        title: 'Backtesting Job',
        indexed: false,
        hide: true,
        tableHidden: true,
        jsonHidden: true,
    },
    // hide bt fields until we can figure out what can safetly be removed
    'data.mode': {
        variable: 'mode',
        field: 'data.mode',
        indexed: false,
        hide: true,
        tableHidden: true,
        jsonHidden: true,
    },
})
    .map(([k, v]) => ({ ...v, field: v.field || `_source.${v.variable}` }))
    .reduce((a, c) => ({ ...a, [c.variable]: c }), {});

const DefaultMapping = {
    'bro_log.id.orig_h': ['bro_log.id.orig_h'],
    'bro_log.id.orig_p': ['bro_log.id.orig_p'],
    'bro_log.id.resp_h': ['bro_log.id.resp_h'],
    'bro_log.id.resp_p': ['bro_log.id.resp_p'],
};

// the columns in this list needs to be 'indexed'
const AllMapping = {
    'bro_log.id.orig_h': ['bro_log.id.orig_h', 'bro_log.x_originating_ip', 'bro_log.xorig', 'bro_log.tx_hosts'],
    'bro_log.id.orig_p': ['bro_log.id.orig_p', 'bro_log.tx_port'],
    'bro_log.id.resp_h': ['bro_log.id.resp_h', 'bro_log.rx_hosts'],
    'bro_log.id.resp_p': ['bro_log.id.resp_p', 'bro_log.rx_port'],
};

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';
const prefix = (cmc) => (MC2CHECK && cmc ? `mc2/cmcapi/${cmc.name}/` : '');

class MetadataDataService {
    constructor () {
        // we only need one of these
        if (!MetadataDataService.instance) {
            MetadataDataService.instance = this;
        }

        this.sources = {}; // tree of sources and nested columns
        this.fieldSources = {}; // a map of fields with a list of sources
        this.columnsMap = defaults; // table columns
        this.fieldsMap = {}; // easy lookup of fields
        this.fields = []; // ordered list of fields

        this.savedFilters = [];
        this.interval = Moment.duration(10, 'm');
        this.cashAsof = Moment().subtract(2, 'hour');
        this.prefix = '';

        // cached column mappings
        this.columnMapping = DefaultMapping;
        this.doColumnMapping = mapping => {
            this.columnMapping = { ...DefaultMapping, ...mapping };
            return this.columnMapping;
        };
        this.isMapped = v => {
            return this.columnMapping[v] &&
            (this.columnMapping[v].length > 1 || this.columnMapping[v][0] !== v);
        };
        this.groupMapping = groups => {
            if (!groups.length) return undefined;

            return groups
                .map(g => (typeof g === 'string') ? g : g.variable)
                .map(g => this.columnMapping[g] ? this.columnMapping[g][0] : g);
        };
        this.getColumnMapping = () => this.columnMapping;
        this.sortMapping = col => this.columnMapping[col] || col;
        this.demap = f => {
            if (!f || f.disabled) return f;
            if (f.nodes) {
                f.nodes = f.nodes.map(this.demap);
            }
            if (f.variable && this.columnMapping[f.variable]) {
                f = {
                    operator: 'Or',
                    nodes: this.columnMapping[f.variable].map(variable => (
                        { ...f, variable }
                    ))
                };
            }
            return f;
        };
        // before sending filter we need to process:
        // 1. demap variables
        // 2. json stringify
        // 3. url encode
        this.processFilter = filter => {
            if (!filter) return undefined;

            // deep copy so the GUI isn't impacted
            const f = JSON.parse(JSON.stringify(filter));
            return encodeURIComponent(
                JSON.stringify(
                    this.demap(f)
                )
            );
        };

        // metadata fields are made of:
        // 1. defaults defined in this file
        // 2. indexed fields discovered from data in elastic
        // 3. all known fields from discovered sources
        // on MC2 pass cmc or not for fields across all cmc
        this.cacheFields = (refresh, cmc) => {
            // at some point we need to force a refresh
            if (!refresh && this.fields.length > 0 &&
                this.cashAsof.isAfter(Moment().subtract(1, 'hour'))) {
                return Promise.resolve(this.fields);
            }

            const params = {};
            if (MC2CHECK && cmc) {
                params.target_cmc = cmc.name;
            }
            return FetchAPI({
                method: 'GET',
                url: `${MC2CHECK ? 'mc2/' : ''}es/indexed-fields/?${ParamsSerializer(params)}`
            }).then(indexed => {
                return FetchAPI({
                    method: 'GET',
                    url: `${MC2CHECK ? 'mc2/' : ''}metadata/sources/?${ParamsSerializer(params)}`
                }).then(sources => {
                    this.sources = _merge(sources, this.sources || {});
                    // coerce list of indexed fields into a map with some defaults
                    const indexedFields = Object.entries(indexed || {}).reduce((a, [k, v]) => {
                        // we don't want to show keyword fields
                        if (k.endsWith('.keyword')) return a;

                        return {
                            ...a,
                            [k]: {
                                field: `_source.${k}`,
                                variable: k,
                                // apply some defaults - we have the opportunity to override in 'fields' presets
                                // tableHidden: c.type === 'text' || c.type === 'geo_point',
                                // indexed: c.type !== 'geo_point',
                                hide: true,
                                title: _getTitle(k),
                                dataType: v,
                                hint: v === 'keyword' ? 'exact text search'
                                    : v === 'text' ? 'partial text match' : v,
                            },
                        };
                    }, {});
                    // coerce nested map of sources into a similar map
                    const allsources = Object.entries(sources || {}).reduce((a, [k, v]) => {
                        // const sources = (a[k] || {}).hint2 || [];
                        const c = Object.entries(v || {}).reduce((aa, [kk, vv]) => {
                            this.fieldSources[vv.name] = (this.fieldSources[vv.name] || {});
                            this.fieldSources[vv.name][k] = vv.type;
                            return {
                                ...aa,
                                ...{
                                    [vv.name]: {
                                        source: k,
                                        field: `_source.${vv.name}`,
                                        variable: vv.name,
                                        // these may be overridden by the other sources
                                        indexed: indexedFields[vv.name] !== undefined,
                                        hide: true,
                                        title: _getTitle(vv.name),
                                        ...vv
                                    }
                                }
                            };
                        }, {});
                        return { ...a, ...c };
                    }, {});

                    // deep merge in order of precedent
                    this.columnsMap = _merge(allsources, indexedFields, this.columnsMap, defaults);

                    // console.log('allsources', allsources);
                    // console.log('indexedFields', indexedFields);
                    // console.log(this.fieldSources);

                    // fieldsMap is a map keyed by field - which is how the table does column lookups
                    this.fieldsMap = _merge(
                        Object.values(this.columnsMap)
                            .reduce((a, c) => ({ ...a, [c.field]: c }), {}),
                        this.fieldsMap
                    );

                    // fields is an ordered list of columns with visible columns up front
                    this.fields = Object.values(this.columnsMap)
                        .sort((a, b) => {
                            if (a.hide && !b.hide) return 1;
                            if (b.hide && !a.hide) return -1;
                            return (a.order || 0) - (b.order || 0);
                        });

                    this.cashAsof = Moment();
                    return this.fields;
                });
            });
        };

        // start data access
        this.getData = ({
            sort,
            order,
            search,
            start,
            end,
            filter,
            tagFilters = [],
            tagsOp,
            groups,
            hasAlerts,
            sources = [],
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }, next = false) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');

            let page;
            if (next && this.page) {
                page = this.page.sort === sort && this.page.order === order
                    ? this.page.data : undefined;
            }

            const params = {
                sort_col: this.sortMapping(sort),
                sort_order: order === 'asc',
                limit: 50,
                offset: 0,
                json_filter: this.processFilter(filter),
                group: this.groupMapping(groups),
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                has_alerts: hasAlerts,
                terms: search, // ? search.split(' ') : undefined,
                start_time: start,
                end_time: end,
                source: sources,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
                page,
            };

            this.getDataController && this.getDataController.abort();
            this.getDataController = new AbortController();
            return FetchAPI({
                method: 'GET',
                url: `${prefix(cmc)}metadata/activity/?${ParamsSerializer(params)}`,
                signal: this.getDataController.signal,
            }).then(res => {
                res = res || {};
                let data = res ? res.logs || [] : [];
                // page must be associated with a sort order -
                // defense against the dark arts of premature scroll events
                this.page = {
                    sort,
                    order,
                    data: data.length ? data[data.length - 1].sort : 0,
                };
                let since;
                // loop through data. once is enough
                data = data.map(d => {
                    since = !since || since.isBefore(d._source.timestamp)
                        ? Moment(d._source.timestamp) : since;
                    return ({ ...d, id: _get(d, '_source.bricata.event_uuid', d.id) });
                });
                return {
                    data,
                    since: since ? since.utc().toISOString() : since,
                    asof: res.asof,
                    timedOut: res.timed_out,
                    took: res.took,
                    total: res.total,
                };
            });
        };

        this.getTimeline = ({
            sources = [],
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            hasAlerts,
            controller,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');

            const params = {
                json_filter: this.processFilter(filter),
                terms: search ? search.split(' ') : undefined,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                source: sources,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
                has_alerts: hasAlerts,
            };
            return FetchAPI({
                method: 'GET',
                url: `${prefix(cmc)}metadata/timeline/?${ParamsSerializer(params)}`,
                signal: controller ? controller.signal : undefined
            }).then(res => {
                const buckets = _get(res, 'timeline.aggregations.timeline.buckets', []);
                this.interval = buckets.length > 1
                    ? Moment.duration(Moment(buckets[1].key_as_string)
                        .diff(Moment(buckets[0].key_as_string)))
                    : Moment.duration(10, 'm');
                buckets.sort((a, b) => this.timestamp(a).getTime() - this.timestamp(b).getTime());
                const last = buckets.length > 0 ? buckets[buckets.length - 1] : {};
                return {
                    objects: buckets,
                    start,
                    end: this.end(last),
                };
            });
        };

        this.GetLazyTimeline = ({
            sources = [],
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            hasAlerts,
            took,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
            cancel,
            interval,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                terms: search ? search.split(' ') : undefined,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                source: sources,
                has_alerts: hasAlerts,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
                took,
                interval,
            };
            // new timeline, cancel old controller
            if (cancel) {
                this.timelineController && this.timelineController.abort();
                this.timelineController = new AbortController();
            }
            return FetchAPI({
                method: 'GET',
                url: `${prefix(cmc)}metadata/timeline/lazy/?${ParamsSerializer(params)}`,
                signal: this.timelineController.signal,
            }).then(res => {
                if (!res) return { controller: this.timelineController };
                const buckets = _get(res, 'timeline.aggregations.timeline.buckets', []);
                this.interval = buckets.length > 1
                    ? Moment.duration(Moment(buckets[1].key_as_string)
                        .diff(Moment(buckets[0].key_as_string)))
                    : Moment.duration(10, 'm');
                buckets.sort((a, b) => this.timestamp(a).getTime() - this.timestamp(b).getTime());
                return {
                    objects: buckets,
                    next: res.next,
                    start: res.start,
                    interval: res.interval,
                    controller: this.timelineController,
                };
            });
        };

        this.getGeoHistory = ({
            search,
            start,
            end,
            filter,
            tagFilters = [],
            tagsOp,
            sources = [],
            page,
            startover,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve({});

            const params = {
                json_filter: this.processFilter(filter),
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                terms: search ? search.split(' ') : undefined,
                start_time: start,
                end_time: end,
                source: sources,
                geo_page: page,
                has_alerts: hasAlerts,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            // train is leaving the station, abort all other activity and create a new controller
            if (startover) {
                this.geoController && this.geoController.abort();
                this.geoController = new AbortController();
            }
            return FetchAPI({
                method: 'GET',
                url: `${prefix(cmc)}metadata/geo/history/?${ParamsSerializer(params)}`,
                signal: this.geoController.signal,
            }).then(res => {
                return { ...res, controller: this.geoController };
            });
        };

        this.getGroups = ({
            order,
            sources = [],
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            groups,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc
        }, cancelable = true) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                sort_order: order === 'asc',
                json_filter: this.processFilter(filter),
                terms: search ? search.split(' ') : undefined,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                group: groups.length > 0 ? this.groupMapping(groups) : ['bricata.event_source'],
                source: sources,
                limit: 111, // The backend will only fetch up to 111 groups. no need to page.
                has_alerts: hasAlerts,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            if (cancelable) {
                this.groupsController && this.groupsController.abort();
            }
            this.groupsController = new AbortController();
            return FetchAPI({
                method: 'GET',
                url: `${prefix(cmc)}metadata/groups/?${ParamsSerializer(params)}`,
                signal: this.groupsController.signal,
            }).then(res => {
                const rows = (res || [])
                    .map(a => ({
                        ..._get(a, 'latest[0]', {}),
                        ...{ groups: a.keys, key: _groupKey(a), count: a.count },
                    }));
                return {
                    data: rows,
                    page: 0,
                    totalCount: rows.length,
                    controller: this.groupsController
                };
            });
        };

        this.getSelectMtdts = ({ start, uuids, sort, order, cmc }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const p = {
                start_time: start,
                sort_col: this.sortMapping(sort),
                sort_order: order === 'asc',
            };
            // this guy and getData will cancel eachother
            this.getDataController && this.getDataController.abort();
            this.getDataController = new AbortController();
            return FetchAPI({
                method: 'PUT',
                url: `${prefix(cmc)}metadata/uuids/?${ParamsSerializer(p)}`,
                body: uuids,
                signal: this.getDataController.signal,
            })
                .then(res => {
                    if (!res) res = { logs: [] };
                    return {
                        data: (res.logs || []).map((a, i) => ({ ...a, remove: i })),
                        page: 0,
                        totalCount: (res.logs || []).length,
                    };
                });
        };

        this.getConnections = ({
            uuid,
            start,
            end,
            cmc,
            backtesting,
            btuuid
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const p = {
                start_time: start,
                end_time: end,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? btuuid : undefined,
            };
            return FetchAPI({
                method: 'GET',
                url: `${prefix(cmc)}metadata/connections/${encodeURIComponent(uuid)}/?${ParamsSerializer(p)}`,
            });
        };

        this.getSources = (cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'GET',
                url: `${prefix(cmc)}metadata/sources/`,
            });
        };

        // TODO - deal with no cmc (across all cmc)
        this.getObserved = (cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'GET',
                url: `${prefix(cmc)}metadata/observed/`,
            });
        };

        this.filterFromGroups = (mtdt, groups = []) => {
            return {
                operator: 'And',
                nodes: groups.reduce((a, c) => {
                    const value = this.value(mtdt, c);
                    return !Array.isArray(value)
                        ? [...a, ({ variable: c.variable, value: `${value}` })]
                        : [...a, ...value.map(v => ({ variable: c.variable, value: `${v}` }))];
                }, [])
            };
        };

        this.cacheSavedFilters = () => {
            return FetchAPI({ method: 'GET', url: 'named_storage/metadata/' }).then(res => {
                this.savedFilters = res || [];
                return this.savedFilters;
            });
        };

        this.deleteSavedFilter = uuid => {
            return FetchAPI({ method: 'DELETE', url: `named_storage/metadata/${encodeURIComponent(uuid)}/` })
                .then(this.cacheSavedFilters);
        };

        this.upsertSavedFilter = filter => {
            return FetchAPI({ method: 'PUT', url: 'named_storage/metadata/', body: filter })
                .then(this.cacheSavedFilters);
        };

        this.getSavedViews = () => {
            return FetchAPI({ method: 'GET', url: 'named_storage/metadata-cols/' });
        };

        this.upsertSavedView = view => {
            return FetchAPI({ method: 'PUT', url: 'named_storage/metadata-cols/', body: view })
                .then(this.getSavedViews);
        };

        this.deleteSavedView = uuid => {
            return FetchAPI({
                method: 'DELETE',
                url: `named_storage/metadata-cols/${encodeURIComponent(uuid)}/`,
            }).then(this.getSavedViews);
        };

        this.tagMtdt = (tag, mtdt, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'POST',
                url: `${prefix(cmc)}metadata/${encodeURIComponent(mtdt._index)}/${mtdt._id}/${encodeURIComponent(tag)}/`
            });
        };

        this.untagMtdt = (tag, mtdt, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'DELETE',
                url: `${prefix(cmc)}metadata/${encodeURIComponent(mtdt._index)}/${mtdt._id}/${encodeURIComponent(tag)}/`
            });
        };

        // from details panel
        this.tagSources = (tag, uuids, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'POST',
                url: `${prefix(cmc)}metadata/_uuids/${encodeURIComponent(uuids)}/${encodeURIComponent(tag)}/`
            });
        };

        this.untagSources = (tag, uuids, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'DELETE',
                url: `${prefix(cmc)}metadata/_uuids/${encodeURIComponent(uuids)}/${encodeURIComponent(tag)}/`
            });
        };

        this.tagMtdts = (tag, {
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            hasAlerts,
            sources = [],
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                terms: search ? search.split(' ') : undefined,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                has_alerts: hasAlerts,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
                source: sources,
            };
            return FetchAPI({ method: 'POST', url: `${prefix(cmc)}metadata/connections/${tag}/?${ParamsSerializer(params)}` });
        };

        this.untagMtdts = (tag, {
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            hasAlerts,
            sources = [],
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                terms: search ? search.split(' ') : undefined,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                has_alerts: hasAlerts,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
                source: sources,
            };
            return FetchAPI({ method: 'DELETE', url: `${prefix(cmc)}metadata/connections/${tag}/?${ParamsSerializer(params)}` });
        };

        this.setMtdtMitre = (eventuuid, mitre, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'PUT',
                url: `${prefix(cmc)}metadata/mitre/_uuids/${eventuuid}/${mitre}/`,
            });
        };

        this.clearMtdtMitre = (eventuuid, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'DELETE',
                url: `${prefix(cmc)}metadata/mitre/_uuids/${eventuuid}/-remove`,
            });
        };

        this.setMtdtsMitre = (mitre, {
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            hasAlerts,
            sources = [],
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                terms: search ? search.split(' ') : undefined,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                has_alerts: hasAlerts,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
                source: sources,
            };
            return FetchAPI({ method: 'PUT', url: `${prefix(cmc)}metadata/mitre/${mitre.id}/?${ParamsSerializer(params)}` });
        };

        this.clearMtdtsMitre = ({
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            hasAlerts,
            sources = [],
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                terms: search ? search.split(' ') : undefined,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                has_alerts: hasAlerts,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
                source: sources,
            };
            return FetchAPI({ method: 'DELETE', url: `${prefix(cmc)}metadata/mitre/?${ParamsSerializer(params)}` });
        };

        this.getGroupsController = () => { return this.groupsController };

        this.generateReport = ({
            sort,
            order,
            search,
            start,
            end,
            filter,
            tagFilters = [],
            tagsOp,
            hasAlerts,
            groups,
            sources = [],
            columns,
            format,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const p = {
                sort_col: this.sortMapping(sort),
                sort_order: order === 'asc',
                json_filter: this.processFilter(filter),
                group: this.groupMapping(groups),
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                has_alerts: hasAlerts,
                terms: search ? search.split(' ') : undefined,
                start_time: start,
                end_time: end,
                source: sources,
                format: format.key,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            return ReportsService.downloadDataReport('metadata', p, columns, this.cmc)
                .then(res => {
                    let filename = 'metadata';
                    if (format.key === 'csv') {
                        filename += '.csv';
                    }
                    SaveFile(res, filename, format.type);
                });
        };

        // widget handlers
        this.getMtdtGroups = ({
            filter,
            start,
            end,
            group,
            limit = 10,
            source,
            tags,
            tags_op, // eslint-disable-line camelcase
            order,
            backtesting,
            selectedBTJobs,
            jobCreated,
            dns
        }, controller) => {
            const p = {
                start_time: start,
                end_time: end,
                json_filter: this.processFilter(filter),
                group: this.groupMapping(group),
                limit,
                source,
                tags,
                tags_op, // eslint-disable-line camelcase
                sort_order: order === 'Least',
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
                dns
            };
            return FetchAPI({
                method: 'GET',
                url: `widgets/metadatagroups/?${ParamsSerializer(p)}`,
                signal: controller.signal,
            });
        };

        this.getMtdtTimelineGroups = ({
            filter,
            start,
            end,
            group,
            limit = 10,
            source,
            tags,
            tags_op, // eslint-disable-line camelcase
            order,
            backtesting,
            selectedBTJobs,
            jobCreated,
            dns
        }) => {
            const params = {
                start_time: start,
                end_time: end,
                json_filter: this.processFilter(filter),
                group,
                limit,
                source,
                tags,
                tags_op, // eslint-disable-line camelcase
                dns,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                sort_order: order === 'Least',
                job_created: jobCreated,
            };
            return FetchAPI({
                method: 'GET',
                url: `widgets/metadatagroups/timeline/?${ParamsSerializer(params)}`
            });
        };

        this.getMtdtTimelineSeries = ({
            start,
            end,
            series = [],
            targets,
            limit,
            source = ['conn'],
            backtesting,
            selectedBTJobs,
            jobCreated,
        }, controller) => {
            const params = {
                start_time: start,
                end_time: end,
                target_cmc: targets,
                source,
                limit,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            // deep copy so the GUI isn't impacted
            const _series = JSON.parse(JSON.stringify(series))
                .map(s => ({
                    ...s,
                    json_filter: s.json_filter ? JSON.stringify(this.demap(s.json_filter)) : undefined,
                }));
            return FetchAPI({
                method: MC2CHECK ? 'GET' : 'PUT',
                url: `${MC2CHECK ? 'mc2/' : ''}widgets/mtdttimeline/?${ParamsSerializer(params)}`,
                body: _series,
                signal: controller.signal,
            });
        };

        this.getUserAgents = ({
            filter,
            start,
            end,
            limit = 42,
            order,
            xua,
            agg,
            search,
            group,
            backtesting,
            selectedBTJobs,
            jobCreated,
        }) => {
            const params = {
                start_time: start,
                end_time: end,
                json_filter: this.processFilter(filter),
                limit,
                xua,
                agent_agg: agg,
                agent_search: search,
                sort_order: order === 'Least',
                agent_full: group === 'full' || undefined,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            return FetchAPI({
                method: 'GET',
                url: `metadata/agents/?${ParamsSerializer(params)}`
            });
        };

        this.getMtdtCounts = ({
            filter,
            start,
            end,
            targets,
            source,
            backtesting,
            selectedBTJobs,
            jobCreated,
            limit = 42,
        }, controller) => {
            if (!controller) return Promise.resolve();
            const params = {
                start_time: start,
                end_time: end,
                json_filter: this.processFilter(filter),
                limit,
                target_cmc: targets,
                source,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            return FetchAPI({
                method: 'GET',
                url: `mc2/widgets/mtdtcounts/?${ParamsSerializer(params)}`,
                signal: controller.signal,
            });
        };

        this.cancelAll = () => {
            this.getDataController && this.getDataController.abort();
            this.groupsController && this.groupsController.abort();
            this.geoController && this.geoController.abort();
            this.timelineController && this.timelineController.abort();
        };

        // normalizing functions
        this.value = (d, c) => !c ? '' : c.getter ? c.getter(d) : _get(d, c.field, '');

        this.count = (d) => { return d ? d.doc_count : undefined };

        this.timestamp = (d) => { return d ? new Date(d.key_as_string) : undefined };

        this.end = (d) => { return d ? Moment(d.key_as_string).add(this.interval).toDate() : undefined };

        this.toEnglish = (f, html, sensorNames, assets, jobs) => FilterToEnglish(
            f,
            { map: this.columnsMap, sensorNames: (sensorNames || {}), assets: (assets || {}), jobs: (jobs || {}) },
            html
        );

        this.key = d => _groupKey(d);

        return MetadataDataService.instance;
    }
}

// helpers
function _groupKey (d) {
    return (d.keys || [])
        .sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
        .map(g => `${g.key} = ${g.value}`)
        .join(', ');
}

// why rewrite when you can just copy/paste?
function _getTitle (name) {
    const prefix = { bricata: true, bro_log: true };
    const fna = name.split('.');
    if (fna.length > 1 && !!prefix[fna[0]]) fna.shift();
    let txt = false;
    if (fna.length > 1 && fna[fna.length - 1] === 'txt') {
        txt = true;
        fna.pop();
    }
    // probably .replace with some cool regex would be shorter...
    let fn = fna.map(e => Acronym(e)
        .split(' ')
        .filter(ee => ee.length > 0)
        .map(ee => ee[0].toUpperCase() + ee.slice(1))
        .join(' '))
        .join(' ');
    if (txt) fn += '(txt)';
    return fn;
}

const MetadataService = new MetadataDataService();

export {
    MetadataService as default,
    DefaultMapping,
    AllMapping,
};
